import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  appOpen: false,
  menuOpen: false,
  activeMenu: "home",
  activePage: "/",
  activePatientSidebarGroup: "photo",
  serverAddress: process.env.REACT_APP_BASE_URL,

  // Toast
  toastVisibilityStatus: false,
  toastType: "success",
  toastTitle: "Success!",
  toastMessage: "Was successful",
  toastDuration: 5000,

  // Loader
  loaderProcesses: 0,

  // DataTable
  itemsPerPage: 10,
  homeDefaultSort: { by: "dateModified", ascending: false },
  allEntryDefaultSort: { by: "dateModified", ascending: false },
  entryDefaultSort: { by: "dateModified", ascending: false },
  patientDefaultSort: { by: "dateModified", ascending: false },
  entryThumbnailDefaultSort: { by: "dateModified", ascending: false },
};

export const applicationStateSlice = createSlice({
  name: "applicationState",
  initialState,
  reducers: {
    setAppOpen: (state, action) => {
      state.appOpen = action.payload;
    },

    setMenuOpen: (state, action) => {
      state.menuOpen = action.payload;
    },

    setActiveMenu: (state, action) => {
      state.activeMenu = action.payload;
    },

    setActivePage: (state, action) => {
      state.activePage = action.payload;
    },

    setActivePatientSidebarGroup: (state, action) => {
      state.activePatientSidebarGroup = action.payload;
    },

    setServerAddress: (state, action) => {
      state.serverAddress = action.payload;
    },

    // Toast
    setToastVisibilityStatus: (state, action) => {
      state.toastVisibilityStatus = action.payload;
    },

    setToastType: (state, action) => {
      state.toastType = action.payload;
    },

    setToastTitle: (state, action) => {
      state.toastTitle = action.payload;
    },

    setToastMessage: (state, action) => {
      state.toastMessage = action.payload;
    },

    setToastDuration: (state, action) => {
      state.toastDuration = action.payload;
    },

    // Loader
    setLoaderProcessesIncrement: (state) => {
      state.loaderProcesses = state.loaderProcesses + 1;
    },

    setLoaderProcessesDecrement: (state) => {
      state.loaderProcesses = state.loaderProcesses - 1;
    },

    resetLoaderProcesses: (state) => {
      state.loaderProcesses = initialState.loaderProcesses;
    },

    // DataTable
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload;
    },

    setHomeDefaultSort: (state, action) => {
      state.homeDefaultSort = action.payload;
    },

    setAllEntryDefaultSort: (state, action) => {
      state.allEntryDefaultSort = action.payload;
    },

    setEntryDefaultSort: (state, action) => {
      state.entryDefaultSort = action.payload;
    },

    setPatientDefaultSort: (state, action) => {
      state.patientDefaultSort = action.payload;
    },

    setEntryThumbnailDefaultSort: (state, action) => {
      state.entryThumbnailDefaultSort = action.payload;
    },

    partialResetApplication: (state) => {
      state.appOpen = initialState.appOpen;
      state.menuOpen = initialState.menuOpen;
      state.activeMenu = initialState.activeMenu;
      state.activePage = initialState.activePage;
      state.activePatientSidebarGroup = initialState.activePatientSidebarGroup;

      state.toastVisibilityStatus = initialState.toastVisibilityStatus;
      state.toastType = initialState.toastType;
      state.toastTitle = initialState.toastTitle;
      state.toastMessage = initialState.toastMessage;
      state.toastDuration = initialState.toastDuration;

      state.loaderProcesses = initialState.loaderProcesses;
    },

    resetApplication: (state) => {
      state.appOpen = initialState.appOpen;
      state.menuOpen = initialState.menuOpen;
      state.activeMenu = initialState.activeMenu;
      state.activePage = initialState.activePage;
      state.activePatientSidebarGroup = initialState.activePatientSidebarGroup;
      state.serverAddress = initialState.serverAddress;

      state.toastVisibilityStatus = initialState.toastVisibilityStatus;
      state.toastType = initialState.toastType;
      state.toastTitle = initialState.toastTitle;
      state.toastMessage = initialState.toastMessage;
      state.toastDuration = initialState.toastDuration;

      state.loaderProcesses = initialState.loaderProcesses;

      state.itemsPerPage = initialState.itemsPerPage;
      state.homeDefaultSort = initialState.homeDefaultSort;
      state.allEntryDefaultSort = initialState.allEntryDefaultSort;
      state.entryDefaultSort = initialState.entryDefaultSort;
      state.patientDefaultSort = initialState.patientDefaultSort;
      state.entryThumbnailDefaultSort = initialState.entryThumbnailDefaultSort;
    },
  },
});

export const {
  setAppOpen,
  setMenuOpen,
  setActiveMenu,
  setActivePage,
  setActivePatientSidebarGroup,

  setServerAddress,

  setToastVisibilityStatus,
  setToastType,
  setToastTitle,
  setToastMessage,
  setToastDuration,

  setLoaderProcessesIncrement,
  setLoaderProcessesDecrement,
  resetLoaderProcesses,

  setItemsPerPage,
  setHomeDefaultSort,
  setAllEntryDefaultSort,
  setEntryDefaultSort,
  setPatientDefaultSort,
  setEntryThumbnailDefaultSort,

  partialResetApplication,
  resetApplication,
} = applicationStateSlice.actions;

export const selectAppOpen = (state) => state.applicationState.appOpen;
export const selectMenuOpen = (state) => state.applicationState.menuOpen;
export const selectActiveMenu = (state) => state.applicationState.activeMenu;
export const selectActivePage = (state) => state.applicationState.activePage;
export const selectActivePatientSidebarGroup = (state) =>
  state.applicationState.activePatientSidebarGroup;
export const selectServerAddress = (state) =>
  state.applicationState.serverAddress;

// Toast
export const selectToastVisibilityStatus = (state) =>
  state.applicationState.toastVisibilityStatus;
export const selectToastType = (state) => state.applicationState.toastType;
export const selectToastTitle = (state) => state.applicationState.toastTitle;
export const selectToastMessage = (state) =>
  state.applicationState.toastMessage;
export const selectToastDuration = (state) =>
  state.applicationState.toastDuration;

// Loader
export const selectLoaderProcesses = (state) =>
  state.applicationState.loaderProcesses;

// DataTable
export const selectItemsPerPage = (state) =>
  state.applicationState.itemsPerPage;
export const selectHomeDefaultSort = (state) =>
  state.applicationState.homeDefaultSort;
export const selectAllEntryDefaultSort = (state) =>
  state.applicationState.allEntryDefaultSort;
export const selectEntryDefaultSort = (state) =>
  state.applicationState.entryDefaultSort;
export const selectPatientDefaultSort = (state) =>
  state.applicationState.patientDefaultSort;
export const selectEntryThumbnailDefaultSort = (state) =>
  state.applicationState.entryThumbnailDefaultSort;

export default applicationStateSlice.reducer;
